import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, Subject, of } from "rxjs";
import { GetGridApiParams, GetTotalLineApiParams } from "./pharma-page.model";
import { AppService } from "../app.service";
import { Store } from "@ngrx/store";
import { AppState, stateGetter } from "../store/state/app.state";
@Injectable({
  providedIn: "root",
})
export class PharmaDataService {
  apiEP: string = window.apiUrl;
  excelExportSource = new Subject<any>();
  onExcelExport = this.excelExportSource.asObservable();
  auditDataSource = new Subject<any>();
  onAuditDSChange = this.auditDataSource.asObservable();
  dpClicked = new Subject<any>();
  onDpClick = this.dpClicked.asObservable();
  pharmaFullExportInProgress = new Subject<any>();
  pharmaDSMAccessCheck = new Subject<any>();
  onPharmaFullExportInProgress = this.pharmaFullExportInProgress.asObservable();
  public pharmaAccess = {
    hasDSMAccess: false,
  };
  constructor(
    private http: HttpClient,
    private appService: AppService,
    private store: Store<AppState>,
  ) {
    this.store
      .select(stateGetter("globalHeader", "globalSettings", "pharmaAccess"))
      .subscribe((data: any) => {
        if (data && data.hasPharmaDSMAccess) {
          this.pharmaAccess.hasDSMAccess = data.hasPharmaDSMAccess;
        }
        this.pharmaDSMAccessCheck.next();
      });
  }

  superimposeChartFilters(chartFilters, facetFilters) {
    let finalFacetFilter = { ...facetFilters };
    for (const filter in chartFilters) {
      if (chartFilters[filter]) {
        finalFacetFilter = {
          ...finalFacetFilter,
          [filter]: [chartFilters[filter]],
        };
      }
    }

    return finalFacetFilter;
  }

  getPharmaStateExtraInfo(keys: Array<string>, selections: any) {
    const extra_info = {};
    keys.forEach((key) => {
      extra_info[key] = selections[key] ? selections[key] : {};
    });
    return extra_info;
  }

  changeDataPoint(data, selectedCell, apiResponseData?) {
    const auditCols = [
      "moa",
      "indications",
      "target",
      "approval_date",
      "drug_type",
      "api",
      "delivery_mechanism",
      "expiry_date",
    ];
    if (apiResponseData) {
      this.dpClicked.next({
        data,
        selectedCell,
        auditCols,
        api_response: apiResponseData,
        isLoading: false,
      });
      return;
    }
    const columnName = selectedCell.substring(selectedCell.indexOf("|") + 1);
    if (!data || !data.dataObj || !data.dataObj[columnName]) {
      this.dpClicked.next({ data, selectedCell, auditCols, isLoading: false });
      return;
    }
    this.dpClicked.next({ data, selectedCell, auditCols, isLoading: true });
  }

  getAuditTrail(ctagid, columnName, col_value) {
    const params = {
      ctagid: ctagid,
      col: columnName,
      col_value: col_value,
    };
    return this.http.post(
      `${this.apiEP}/pharma_api/get_audit_trial_data`,
      params,
    );
  }

  getDrugsByCompanyData(params): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_drugs_by_company`,
      params,
    );
  }

  getDrugsByMOA(params): Observable<any> {
    return this.http.post(`${this.apiEP}/pharma_api/get_drugs_by_moa`, params);
  }

  getDrugsByPhase(params): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_drugs_by_phase`,
      params,
    );
  }

  getDrugsByIndication(params): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_drugs_by_indication`,
      params,
    );
  }

  getDrugsByTherapeuticArea(params): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_drugs_by_therapeutic_area`,
      params,
    );
  }

  getGridData(params: GetGridApiParams): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_pharma_grid_data`,
      params,
    );
  }

  getTotalLineData(params: GetTotalLineApiParams): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_total_line_data`,
      params,
    );
  }

  getBreakoutData(params): Observable<any> {
    return this.http.post(`${this.apiEP}/pharma_api/get_breakout_data`, params);
  }
  getFacetData(params): Observable<any> {
    return this.http.post(`${this.apiEP}/pharma_api/get_facet_data`, params);
  }

  getIndicationFacetData(params): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_indication_facet_data`,
      params,
    );
  }

  getMoaFacetData(params): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_moa_facet_data`,
      params,
    );
  }

  getDrugClassFacetData(params): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_drug_class_facet_data`,
      params,
    );
  }

  getTargetFacetData(params): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_target_facet_data`,
      params,
    );
  }

  getWatchlists(): Observable<any> {
    return this.http.get(`${this.apiEP}/watcher/get_watchlist/`);
  }

  saveCurrentFilter(payload): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/save_user_settings`,
      payload,
    );
  }

  getAllSavedFilters(): Observable<any> {
    return this.http.get(
      `${this.apiEP}/pharma_api/get_user_filters?page_name=database`,
    );
  }

  getSavedFilterObject(id): Observable<any> {
    return this.http.get(
      `${this.apiEP}/pharma_api/get_filter_data?filter_id=${id}`,
    );
  }

  deleteSavedFilter(id): Observable<any> {
    const params = {
      filter_id: id,
      is_delete: 1,
    };
    return this.http.put(`${this.apiEP}/pharma_api/delete_filter_data`, params);
  }

  updateFilterObject(payload): Observable<any> {
    return this.http.put(
      `${this.apiEP}/pharma_api/update_filter_data`,
      payload,
    );
  }

  getDpCallDetails(payload): Observable<any> {
    return this.http.get(
      `${this.apiEP}/pharma_api/get_period_col_dp_details?companyid=${
        payload.company_id
      }&dapparameterid=${payload.dapparameterid}&period=${
        payload.period
      }&period_basis=${payload.period_basis}&period_type=${
        payload.period_type
      }&growth_type=${payload.growthType}&view_by=${payload.viewby}${
        payload.viewby === "revision" ? `&revision=${payload.revision}` : ""
      }`,
    );
  }

  getNonPeriodDpCallDetials(payload): Observable<any> {
    return this.http.get(
      `${this.apiEP}/pharma_api/get_non_period_col_dp_details?companyid=${payload.companyid}&dapparameterid=${payload.dapparameterid}&revenue_type=${payload.revenue_type}&col=${payload.col}&ctagid=${payload.ctagid}&period_basis=${payload.period_basis}&is_breakout=${payload.is_breakout}`,
    );
  }
  getAbsoluteList(from: number, to: number) {
    const currentYear = new Date().getFullYear() - 1;
    let counter = from;
    const periodList = [];
    while (counter <= to) {
      const delta = currentYear + counter;
      periodList.push(`CY-${delta.toString()}`);
      counter = counter + 1;
    }
    return periodList;
  }

  exportDrugDashboard(payload, params): Observable<any> {
    return this.http.post(`${this.apiEP}/addin/export?${params}`, payload);
  }
  logUserAction(feature, extras, source?) {
    this.appService.logUserAction(
      {},
      "",
      "",
      `BIOPHARMA_${feature}`,
      extras,
      "",
      source ? source : "BIOPHARMA_DB",
    );
  }

  exportFullDrugDashboard(payload, params): Observable<any> {
    return this.http.post(
      `${this.apiEP}/addin/pharma_full_download?${params}`,
      payload,
    );
  }

  getBiopharmaSearchResults(payload) {
    if (payload.query === "") {
      return of({ data: [] });
    }
    if (Object.keys(payload.context).length === 0) {
      payload = {
        query: payload.query,
      };
    }
    ///pharma_api/get_moa_facet_data
    return this.http.post(
      `${this.apiEP}/pharma_api/pharma_facet_search`,
      payload,
    );
  }

  getDrugSalesChartData(payload) {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_sales_chart_data`,
      payload,
    );
  }

  getSalesGrowthData(payload) {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_sales_growth_grid`,
      payload,
    );
  }

  getRelatedMetricsData(payload) {
    payload["geobreakout_data_check"] = true;
    return this.http.post(
      `${this.apiEP}/pharma_api/get_related_metrics_data`,
      payload,
    );
  }

  getDrugOverallSummary(cTagId) {
    return this.http.get(
      `${this.apiEP}/pharma_api/get_drug_summary?ctag_id=${cTagId}`,
    );
  }

  getGeoMixData(payload) {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_geo_grid_data`,
      payload,
    );
  }

  getReveueByIndicationData(payload) {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_revenue_indication_grid`,
      payload,
    );
  }

  getDrugPageSummarySection(cTagId, currency): Observable<any> {
    const uri = currency.length
      ? `${this.apiEP}/pharma_api/drugpage_details?ctagid=${cTagId}&currency=${currency}`
      : `${this.apiEP}/pharma_api/drugpage_details?ctagid=${cTagId}`;
    return this.http.get(uri);
  }

  getDefaultPeriod(ctagId) {
    return this.http.get(
      `${this.apiEP}/pharma_api/get_drug_periods?ctagid=${ctagId}`,
    );
  }

  getDistributionPageData(params) {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_drug_distribution_data`,
      params,
    );
  }
  getDistributionPageReaffirmFlag(params) {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_pharma_reaffirm`,
      params,
    );
  }
  getDistributionPageSourcesData(params) {
    return this.http.post(`${this.apiEP}/pharma_api/get_drug_sources`, params);
  }
  getDistributionPageFilterData(ctagId) {
    return this.http.get(
      `${this.apiEP}/pharma_api/get_ctagid_filter_data?filter_name=${ctagId}&page_name=distribution`,
    );
  }
  saveDsitributionPageFilter(dataToSave) {
    return this.http.post("", dataToSave);
  }

  getPharmaFullExportData(payload) {
    payload["geobreakout_data_check"] = true;
    return this.http.post(
      `${this.apiEP}/addin/export?export_type=1&template_type=sales_analyst_export`,
      payload,
    );
  }

  getPharmaDSMDrugParameters(payload): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_dsm_drug_parameters`,
      payload,
    );
  }

  getPharmaDSMDrugDocument(payload): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_dsm_drug_documents`,
      payload,
    );
  }

  getPharmaDSMDocumentSentences(payload): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_dsm_drug_sentences`,
      payload,
    );
  }

  setUserSelectionForDSMDocumentSentences(payload): Observable<any> {
    return this.http.post(
      `${this.apiEP}/pharma_api/get_dsm_record_user_feedback`,
      payload,
    );
  }
}
